/**
 * Form Element Factory
 * 
 * Responsible for creating form element data structures with proper defaults.
 * Each element type has a well-defined schema with sensible default values.
 * 
 * When a new element is dropped into the form, this factory produces the complete
 * data object that gets stored in the Vuex store (formBuilder/formElements).
 * 
 * Note: MAPPABLE_TYPES constant has been moved to the Vuex store 
 * (store/modules/workbench/formBuilder/index.js) for centralized state management.
 * Use the store's isMappableType getter to check if an element type is mappable.
 */

/**
 * Type-specific default data keyed by element type.
 * Common fields (label, description) appear on most types but are listed
 * explicitly so every type is self-documenting.
 */
const TYPE_DEFAULTS = {
  // ── Layout ────────────────────────────────────
  'section-header': {
    label: 'Section Header',
    description: '',
  },
  'clinic-branding': {
    label: 'Your Clinic Name',
    logoFile: '',
    logoFileId: '',
  },
  'note-help': {
    content: '', // plain text
  },
  'image': {
    label: 'Image',
    description: '',
    imageFile: '', // 
    imageFileId: '',
  },

  // ── Text Inputs ───────────────────────────────
  'short-response': {
    label: 'Short Response',
    description: '',
    required: false,
  },
  'long-response': {
    label: 'Long Response',
    description: '',
    required: false,
  },
  'description': {
    content: '', // rich text (HTML)
  },

  // ── Contact Info ──────────────────────────────
  'email': {
    label: 'Email',
    description: '',
    required: false,
  },
  'phone': {
    label: 'Phone Number',
    description: '',
    required: false,
  },

  // ── Selection Inputs ──────────────────────────
  'single-selection': {
    label: 'Single Selection',
    description: '',
    required: false,
    options: ['Option 1', 'Option 2'],
  },
  'multi-selection': {
    label: 'Multi-selection',
    description: '',
    required: false,
    options: ['Option 1', 'Option 2'],
  },
  'checkbox': {
    label: 'Checkbox',
    description: '',
    option: 'Option',
    required: false,
  },
  'dropdown': {
    label: 'Single-selection Dropdown',
    description: '',
    required: false,
    options: ['Option 1', 'Option 2'],
  },
  'yes-no': {
    label: 'Yes / No Question',
    description: '',
    required: false,
  },

  // ── Specialized Inputs ────────────────────────
  'date': {
    label: 'Date',
    description: '',
    required: false,
  },
  'datetime': {
    label: 'Date & Time',
    description: '',
    required: false,
  },
  'time': {
    label: 'Time',
    description: '',
    required: false,
  },
  'numeric': {
    label: 'Numeric',
    description: '',
    required: false,
    isLimited: false,
    min: null,
    max: null,
  },
  'rating': {
    label: 'Rating Scale',
    description: '',
    required: false,
    scaleSize: 5,
    leftLabel: 'Lowest',
    rightLabel: 'Highest',
  },
  'signer': {
    label: 'Signer',
    description: '',
    required: false,
  },
};

/**
 * Note: MAPPABLE_TYPES has been moved to the Vuex store and is now dynamically
 * derived from mappableVariables (backend data).
 * 
 * To check if a type is mappable, use the store getter:
 * this.$store.getters['workbench/formBuilder/isMappableType'](type)
 * 
 * Or in components using mapGetters:
 * ...mapGetters('workbench/formBuilder', ['MAPPABLE_TYPES', 'isMappableType'])
 */

/**
 * Create a complete form element object ready to be stored in formElements[].
 *
 * @param {Object} source - The drag payload or element definition
 *   Must contain at least { type, label }.
 * @returns {Object} A form element with unique id + deep-cloned data defaults.
 */
export function createFormElement(source) {
  const { type, label } = source;
  const defaults = TYPE_DEFAULTS[type] || {};
  const clonedData = deepClone(defaults);

  return {
    type,
    label,
    id: `${type}-${Date.now()}`,
    data: clonedData,
  };
}

/**
 * Get a fresh copy of the default data for a given element type.
 * Useful when an element's type is changed and you want fresh defaults
 * while preserving the id.
 *
 * @param {string} type
 * @returns {Object}
 */
export function getDefaultData(type) {
  return deepClone(TYPE_DEFAULTS[type] || {});
}

/**
 * Check if an element type is mappable.
 * @param {string} type
 * @returns {boolean}
 */
export function isMappableType(type) {
  return MAPPABLE_TYPES.includes(type);
}

// ── helpers ─────────────────────────────────────
function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(deepClone);
  const clone = {};
  for (const key of Object.keys(obj)) {
    clone[key] = deepClone(obj[key]);
  }
  return clone;
}

export default {
  createFormElement,
  getDefaultData,
};
